import PlusIcon from "@assets/svg/PlusIcon";
import ChevronDown from "@assets/svg/ChevronDown";
import SearchIcon from "@assets/svg/SearchIcon";
import GroupIcon from "@assets/svg/GroupIcon";
// @ts-ignore
import ProfileImg from "@assets/images/profilepic.png";
import { useNavigate } from "react-router-dom";
import Phone from "@assets/svg/Phone";
import Email from "@assets/svg/Email";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getHiringCompanyMembers,
  inviteHiringStaff,
} from "@endpoints/hiringCompany/staffMember.service";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import CrossBig from "@assets/svg/CrossBig";
import { useForm } from "react-hook-form";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import dayjs from "@lib/dayjs";
import Spinner from "@components/Spinner/Spinner";
import CustomInput from "@components/CustomInput/CustomInput";
import { colors } from "@utils/theme";
import { RootState } from "@store/reducers";
import {
  HiringCompanyApprovalStatus,
  HiringCompanyType,
} from "@utils/constants";

const AccountMembers = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user, companyDetails } = useSelector(
    (state: RootState) => state.auth
  );
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const initialRef = useRef(false);
  const observer = useRef(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchText, setSearchText] = useState("");
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      inviteModal: false,
      isSubmitting: false,
      firstName: undefined,
      lastName: undefined,
      email: undefined,
      role: HiringCompanyType.TEAM_MEMBER,
    },
  });
  const fields = watch();

  const currentRole = Number(user?.hiringCompanyType);
  const canInvite =
    currentRole === HiringCompanyType.SUPER_ADMIN ||
    currentRole === HiringCompanyType.ADMIN ||
    currentRole === HiringCompanyType.MANAGER;

  const isHiringManager =
    currentRole === HiringCompanyType.MANAGER;

  const roleOptions = [
    { label: "Admin", value: HiringCompanyType.ADMIN },
    { label: "Manager", value: HiringCompanyType.MANAGER },
    { label: "Hiring team member", value: HiringCompanyType.TEAM_MEMBER },
  ];

  const fetchAccountMembers = async (currentPage, searchQuery) => {
    const params: {
      page: number;
      limit: number;
      searchText?: string;
    } = {
      page: currentPage,
      limit: 10,
    };
    if (searchQuery) {
      params.searchText = searchQuery;
    }
    // @ts-ignore
    return getHiringCompanyMembers(params)
      .then((response) => {
        const { data, success } = response;
        if (success) {
          setMembers((prevMembers) =>
            currentPage === 1 ? data.members : [...prevMembers, ...data.members]
          );
          setPage(data.page);
          setHasMore(data.hasMore);
        }
      })
      .catch((error) => {
        enqueueSnackbar(error, {
          variant: "error",
        });
      })
      .finally(() => {
        setLoading(false);
        setIsPaginationLoading(false);
      });
  };

  useEffect(() => {
    console.log("user", user);
    if (initialRef.current) return;
    setLoading(true);
    initialRef.current = true;
    fetchAccountMembers(1, null);
  }, []);

  // add debounce
  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  // for unnecessary api calls
  const debouncedFetch = useCallback(
    debounce((nextPage, search) => {
      // prevent multiple calls while loading
      if (isPaginationLoading) return;
      setIsPaginationLoading(true);
      fetchAccountMembers(nextPage, search || null)?.finally(() =>
        setIsPaginationLoading(false)
      );
    }, 500),
    []
  );

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      setMembers([]); // Clear existing members when searching
      setPage(1); // Reset to first page
      fetchAccountMembers(1, searchValue);
    }, 1000),
    []
  );

  // for infinite scroll
  const lastHistoryElementRef = useCallback(
    (node) => {
      if (isPaginationLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            const nextPage = page + 1;
            debouncedFetch(nextPage, searchText);
          }
        },
        {
          threshold: 0.1,
          rootMargin: "200px",
        }
      );
      if (node) observer.current.observe(node);
    },
    [isPaginationLoading, hasMore, page, debouncedFetch, searchText]
  );

  const handleItemClick = (item) => {
    navigate(`/account/account-members/${item?._id}`, { state: { item } });
  };

  const handleFormSubmit = async () => {
    setValue("isSubmitting", true);
    try {
      await inviteHiringStaff({
        firstName: fields?.firstName,
        lastName: fields?.lastName,
        email: fields?.email,
        role: isHiringManager
          ? HiringCompanyType.TEAM_MEMBER
          : fields?.role ?? HiringCompanyType.TEAM_MEMBER,
      });
      enqueueSnackbar("Member invited successfully", { variant: "success" });
      reset({
        inviteModal: false,
        firstName: undefined,
        lastName: undefined,
        email: undefined,
        role: HiringCompanyType.TEAM_MEMBER,
      });
    } catch (error) {
      enqueueSnackbar("Error inviting member", { variant: "error" });
    } finally {
      setValue("isSubmitting", false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    if (!value || value.trim() === "") {
      // If search is cleared, reset to initial state
      setMembers([]);
      fetchAccountMembers(1, null);
    } else {
      debouncedSearch(value);
    }
  };

  return (
    <div className="flex flex-col overflow-y-hidden h-full rounded-lg bg-white w-full flex-grow p-4">
      {/* Search and filter section */}
      <p className="text-center text-sm font-bold underline">
        {user?.companyName ?? companyDetails?.name} Staff Members
      </p>
      <div className="flex items-center justify-between w-full my-8">
        <div className="flex items-center gap-8">
          {/* Input container */}
          <div className="flex rounded-lg gap-3 items-center justify-between bg-light-white-500 px-3 py-1.5">
            <button className="flex items-center justify-center border bg-white rounded-md px-4 py-1 gap-2">
              <label className="text-charcoal text-sm font-medium">
                Search by
              </label>
              <ChevronDown className={"w-4 h-4"} fill={"#2e2e2e"} />
            </button>
            <input
              className="bg-white text-xs font-medium text-light-black outline-none px-3 py-2 rounded-lg w-80"
              placeholder="Search by Name, Title or Ref number..."
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
            />
            <SearchIcon className={"w-6 h-6 cursor-pointer"} fill={"#2e2e2e"} />
          </div>
        </div>

        {canInvite ? (
          <button
            onClick={() => setValue("inviteModal", true)}
            className="flex items-center justify-center border bg-green-600 rounded-md px-2 py-1 gap-1 text-white font-semibold text-sm"
          >
            <PlusIcon className={"w-4 h-4"} fill={"white"} />
            Invite Members
          </button>
        ) : null}
      </div>

      {canInvite && fields?.inviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-1/3">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-xl font-semibold">Invite Member</h2>
              <button onClick={() => setValue("inviteModal", false)}>
                <CrossBig width={24} height={24} />
              </button>
            </div>
            <div className="bg-[#F9FAFB] px-8 py-6 rounded-2xl">
              <p className="text-sm font-semibold text-[#2E2E2E] mb-4">
                General Information
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  {/* @ts-ignore */}
                  <CustomInput
                    label="First name*"
                    name="firstName"
                    control={control}
                    errors={errors}
                    rules={{
                      required: "This field is required.",
                    }}
                  />
                </div>
                <div>
                  {/* @ts-ignore */}
                  <CustomInput
                    label="Last name*"
                    name="lastName"
                    control={control}
                    errors={errors}
                    rules={{
                      required: "This field is required.",
                    }}
                  />
                </div>
                <div className="col-span-2">
                  {/* @ts-ignore */}
                  <CustomInput
                    label="Company's email address*"
                    name="email"
                    control={control}
                    errors={errors}
                    rules={{
                      required: "This field is required.",
                    }}
                  />
                </div>
              </div>

              <div className="mt-8">
                <p className="text-sm font-semibold text-[#2E2E2E] mb-2">
                  User Role
                </p>
                <label className="text-xs font-medium text-[#2E2E2E]">
                  User role*
                </label>
                <select
                  name="role"
                  value={
                    isHiringManager
                      ? HiringCompanyType.TEAM_MEMBER
                      : fields?.role ?? HiringCompanyType.TEAM_MEMBER
                  }
                  disabled={isHiringManager}
                  onChange={(e) =>
                    setValue("role", Number(e.target.value))
                  }
                  className="mt-2 h-10 w-full rounded-md border border-[#E6E6E6] bg-white px-3 text-xs font-medium text-[#2E2E2E]"
                >
                  {(isHiringManager
                    ? roleOptions.filter(
                        (option) =>
                          option.value === HiringCompanyType.TEAM_MEMBER,
                      )
                    : roleOptions
                  ).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {isHiringManager ? (
                  <p className="text-[11px] text-[#6F6F6F] mt-2">
                    Hiring managers can only invite hiring team members.
                  </p>
                ) : null}
              </div>

              <div className="flex justify-end mt-10">
                <button
                  onClick={handleSubmit(handleFormSubmit)}
                  className="text-sm bg-primary text-white py-2 px-6 rounded-md flex items-center gap-2"
                >
                  {fields.isSubmitting && (
                    <LoadingAnimation color={colors.White} />
                  )}
                  Create user
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-screen">
          <Spinner />
        </div>
      ) : members?.length === 0 ? (
        <div className="flex flex-1 h-screen flex-col items-center justify-center">
          <p className="text-charcoal font-semibold">No Account Member</p>
          <p className="mt-2 mb-5">Invite Members now!</p>
        </div>
      ) : (
        <div className="flex flex-1 h-full flex-col overflow-y-scroll no-scrollbar">
          {members.map((data, index) => (
            <div
              className="flex cursor-pointer flex-col"
              onClick={() => handleItemClick(data)}
              key={data._id}
              ref={index === members.length - 1 ? lastHistoryElementRef : null}
            >
              <div className="bg-[#FAFAFA] flex justify-between text-xs px-5 py-2 cursor-pointer text-[#2E2E2E]">
                <div className="flex items-center w-full justify-between">
                  <div className="w-1/5">
                    <img
                      src={ProfileImg}
                      alt="profile img"
                      className="h-10 w-10 bg-gray-500 rounded-full"
                    />
                  </div>
                  <div className="flex flex-col gap-[6px] w-1/4 text-[#6F6F6F]">
                    <p className="text-[#2E2E2E]">
                      {data.firstName} {data.lastName}
                    </p>
                    <p>{data.agentPosition || "Agent"}</p>
                  </div>
                  <div className="flex flex-col gap-[6px] w-1/4">
                    <p className="flex gap-[5px]">
                      <span>
                        <Phone />
                      </span>
                      {data.phone || "   ----"}
                    </p>
                    <p className="flex gap-[5px]">
                      <span>
                        <Email />
                      </span>
                      {data.email}
                    </p>
                  </div>
                  <p className="w-1/5 text-center">{data.refNo}</p>
                </div>
              </div>
              <div className="flex items-end gap-7 space-y-3 ml-8">
                <div>
                  <GroupIcon className={"w-8 h-8"} />
                </div>
                <div className="flex flex-col flex-1">
                  <div className="flex font-semibold text-xs italic text-[#BABABA] gap-4 mb-[6px]">
                    <p>
                      Received{" "}
                      {dayjs(data.createdAt).local().format("hh:mm A")}
                    </p>
                    <span>
                      {dayjs(data.createdAt).local().format("Do MMMM YYYY")}
                    </span>
                  </div>
                  {
                    data.hiringCompanyApprovalStatus ===
                    HiringCompanyApprovalStatus.REQUESTED ? (
                      <div className="bg-[#FAFAFA] text-[#6F6F6F] flex justify-between text-xs font-medium px-5 py-2 cursor-pointer">
                        <p className="text-[#A43370] text-xs px-[10px] py-1 bg-[#F7DEEC] rounded">
                          {/* Pending {user.firstName} Recruitment Admin Approval */}
                          Pending Approval
                        </p>
                        <p className="w-1/5 text-center">{data.id}</p>
                      </div>
                    ) : data.hiringCompanyApprovalStatus ===
                      HiringCompanyApprovalStatus.ACCEPTED ? (
                      <div className="bg-[#FAFAFA] text-[#6F6F6F] flex justify-between text-xs font-medium px-5 py-2 cursor-pointer">
                        <p className="text-[#1e8528] text-xs px-[10px] py-1 bg-[#dcf7d6] rounded    ">
                          Approved
                          {/* ABC Recruitment Admin Approved{" "} */}
                          {/* <span className="italic text-[#BABABA] text-xs ml-4">
                          by John clark
                        </span> */}
                        </p>
                        <p className="w-1/5 text-center">{data.id}</p>
                      </div>
                    ) : (
                      <div className="bg-[#FAFAFA] text-[#6F6F6F] flex justify-between text-xs font-medium px-5 py-2 cursor-pointer">
                        <p className="text-[#d72417] text-xs px-[10px] py-1 bg-[#fbd9ce] rounded    ">
                          Rejected
                          {/* ABC Recruitment Admin Approved{" "} */}
                          {/* <span className="italic text-[#BABABA] text-xs ml-4">
                          by John clark
                        </span> */}
                        </p>
                        <p className="w-1/5 text-center">{data.id}</p>
                      </div>
                    )
                    // :
                    //  (
                    //   <div className="bg-[#FAFAFA] text-[#6F6F6F] flex justify-between text-xs font-medium px-5 py-2">
                    //     <p className="w-1/4">{data.title}</p>
                    //     <div className="flex w-[40%] gap-2">
                    //       {data.key === "applied" ? (
                    //         <span>{data.mode}</span>
                    //       ) : (
                    //         <>
                    //           <span>{data.time}</span>
                    //           <span>{data.date}</span>
                    //         </>
                    //       )}
                    //     </div>
                    //     <p className="w-1/4">
                    //       {data.key === "call" ? data.number : data.position}
                    //     </p>
                    //     <p className="w-1/4 text-center">{data.id}</p>
                    //   </div>
                    // )
                  }
                </div>
              </div>
              <hr className="border-t-1 border-gray-300 my-4" />
            </div>
          ))}
          {/* Add pagination loader */}
          {isPaginationLoading && (
            // <LoadingAnimation color={colors.White} className="w-4 h-4" />
            <Spinner />
          )}
        </div>
      )}
    </div>
  );
};

export default AccountMembers;
